/**
 * Shared heading-id generation for blog content.
 *
 * app/blog/[slug]/page.tsx injects heading ids into rendered HTML (for
 * scroll-margin anchors); components/ui/table-of-contents.tsx independently
 * extracts headings from the same raw markdown to build TOC links. Both
 * MUST produce identical ids for identical content, including when the
 * same heading text repeats (e.g. two "Conclusion" headings) — otherwise a
 * TOC anchor silently jumps to the first occurrence only.
 */

export interface HeadingTextLike {
  text: string
}

/**
 * Slugify a single heading's text. Not exported — always go through
 * makeHeadingIds so duplicates get their uniqueness suffix.
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

/**
 * Assigns ids to headings in document order, appending -2, -3, … to
 * duplicate slugs so every heading gets a unique, stable id. Callers must
 * pass headings in the same order they appear in the source content —
 * the uniqueness suffixes depend on that order.
 */
export function makeHeadingIds<T extends HeadingTextLike>(headings: T[]): string[] {
  const seen = new Map<string, number>()
  return headings.map(({ text }) => {
    const base = slugify(text)
    const count = (seen.get(base) ?? 0) + 1
    seen.set(base, count)
    return count === 1 ? base : `${base}-${count}`
  })
}
